import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';

const SEOHead = ({ seo, location = null, language = null }) => {
  const { language: globalLanguage } = useLanguage();
  
  // Use passed language or global language
  const currentLang = language || globalLanguage;
  
  // Check if location uses French
  const isFrenchLocation = location?.defaultLanguage === 'fr';
  
  // Get title and description based on language
  let title, description;
  
  if (isFrenchLocation) {
    title = currentLang === 'fr' ? (seo.titleFr || seo.titleEn) : seo.titleEn;
    description = currentLang === 'fr' ? (seo.descriptionFr || seo.descriptionEn) : seo.descriptionEn;
  } else {
    title = currentLang === 'en' ? seo.titleEn : (seo.titleId || seo.titleEn);
    description = currentLang === 'en' ? seo.descriptionEn : (seo.descriptionId || seo.descriptionEn);
  }
  
  const keywords = seo.keywords || '';
  
  // Build structured data for LocalBusiness (if location provided)
  const structuredData = location ? {
    "@context": "https://schema.org",
    "@type": "DanceSchool",
    "name": `Studio Ballet Izora ${location.city}`,
    "description": description,
    "url": window.location.href,
    "logo": `${window.location.origin}/images/logo.jpg`,
    "image": `${window.location.origin}/images/hero.png`,
    "telephone": location.schoolInfo.whatsapp,
    "email": location.schoolInfo.email,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": location.city,
      "addressCountry": location.countryCode
    },
    "geo": {
      "@type": "GeoCoordinates"
    },
    "priceRange": "$$",
    "openingHours": "Mo-Sa 09:00-18:00",
    "sameAs": [
      location.schoolInfo.instagram
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": location.studentsCount?.replace('+', '') || "50"
    }
  } : {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Studio Ballet Izora",
    "description": description,
    "url": window.location.href,
    "logo": `${window.location.origin}/images/logo.jpg`,
    "sameAs": [
      "https://www.instagram.com/studioballetizora"
    ]
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content="index, follow" />
      <meta name="language" content={isFrenchLocation ? (currentLang === 'fr' ? 'French' : 'English') : (currentLang === 'en' ? 'English' : 'Indonesian')} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={window.location.href} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`${window.location.origin}/images/hero.png`} />
      <meta property="og:locale" content={isFrenchLocation ? (currentLang === 'fr' ? 'fr_FR' : 'en_US') : (currentLang === 'en' ? 'en_US' : 'id_ID')} />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={window.location.href} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={`${window.location.origin}/images/hero.png`} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={window.location.href} />
      
      {/* Hreflang for multilingual */}
      <link rel="alternate" hrefLang="en" href={window.location.href} />
      <link rel="alternate" hrefLang="id" href={window.location.href} />
      <link rel="alternate" hrefLang="x-default" href={window.location.href} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default SEOHead;
